import {
  Box,
  HStack,
  Link,
  Select,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';

import { FormatType, getFormatter, TableChart } from '@revelio/replots';

import {
  type CompanyPeekData,
  CompanyPeekTooltip,
} from '../company-details/CompanyPeekTooltip';
import type {
  CompanyMetrics,
  TrendDirection,
} from '../hooks/useCompetitorComposition';
import type { Competitor } from '../hooks/useCompetitors';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { EmployerHealth, RiskLevel } from '../hooks/useEmployerHealth';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import {
  type CompanySentiment,
  SENTIMENT_KEYS,
  SENTIMENT_LABELS,
  type SentimentScores,
} from '../hooks/useSentimentComparison';
import type { WorkHistoryCompany } from '../hooks/useWorkHistoryCompanies';
import { SentimentDetailModal } from '../optimize/SentimentDetailModal';
import { RISK_COLORS_HEX } from '../utils/colors';
import { formatDateRange } from '../utils/formatDateRange';

const pctFormatter = getFormatter(FormatType.PERCENTAGE);
const intFormatter = getFormatter(FormatType.INTEGER);

/** Convert 0–1 normalized sentiment to 0–5 display rating */
const toRating = (value: number): string => (value * 5).toFixed(1);

const slugifyForLinkedIn = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** 0–1 scale: ≥0.8 green, ≥0.7 light green, ≥0.5 orange, <0.5 red */
const getSentimentColor = (value: number): string => {
  if (value >= 0.8) return '#38A169';
  if (value >= 0.7) return '#68D391';
  if (value >= 0.5) return '#DD6B20';
  return '#E53E3E';
};

interface TopSentimentAttr {
  label: string;
  value: number;
}

const getTopSentimentAttrs = (
  scores: SentimentScores,
  count: number
): TopSentimentAttr[] => {
  const entries: TopSentimentAttr[] = [];
  for (const key of SENTIMENT_KEYS) {
    if (key === 'overall') continue;
    const val = scores[key];
    if (typeof val === 'number' && val > 0) {
      entries.push({ label: SENTIMENT_LABELS[key], value: val });
    }
  }
  return entries.sort((a, b) => b.value - a.value).slice(0, count);
};

interface CompetitorRow {
  companyId: number;
  shortName: string;
  longName: string;
  headcount: number | null;
  salary: number | null;
  growthRate: number | null;
  attritionRate: number | null;
  hiringTrend: TrendDirection;
  sentimentOverall: number | null;
  topSentimentAttrs: TopSentimentAttr[];
  stabilityScore: number | null;
  risk: RiskLevel | null;
  isCurrentEmployer: boolean;
  isFormerEmployer: boolean;
  linkedInUrl: string | null;
  hqLocation: string | null;
}

const TREND_LABELS: Record<TrendDirection, string> = {
  growing: '↑ Growing',
  stable: '→ Stable',
  declining: '↓ Declining',
};

interface CompetitorTableProps {
  metrics: CompanyMetrics[];
  sentiments: CompanySentiment[];
  healthData: EmployerHealth[];
  competitors: Competitor[];
  currentCompanyId: number | null;
  historicalCompanyIds?: Set<number>;
  isLoading: boolean;
  workHistoryCompanies?: WorkHistoryCompany[];
  selectedCompanyRcid?: number | null;
  onCompanySelect?: (rcid: number | null) => void;
  /** True when competitors are from RICS industry fallback instead of direct competitor lookup */
  isIndustryFallback?: boolean;
  userSalary?: number;
  userRoleLevel?: RoleLevel | null;
}

const columnHelper = createColumnHelper<CompetitorRow>();

export const CompetitorTable = ({
  metrics,
  sentiments,
  healthData,
  competitors,
  currentCompanyId,
  historicalCompanyIds,
  isLoading,
  workHistoryCompanies,
  selectedCompanyRcid,
  onCompanySelect,
  isIndustryFallback,
  userSalary = 0,
  userRoleLevel,
}: CompetitorTableProps) => {
  const salaryFormatter = useCurrencyFormatter();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalCompanyRcid, setModalCompanyRcid] = useState<number | null>(null);
  const [modalCompanyName, setModalCompanyName] = useState<string | null>(null);

  const sentimentMap = useMemo(
    () => new Map(sentiments.map((s) => [s.companyId, s])),
    [sentiments]
  );

  const onSentimentClick = useCallback(
    (companyId: number, companyName: string) => {
      setModalCompanyRcid(companyId);
      setModalCompanyName(companyName);
      onOpen();
    },
    [onOpen]
  );

  const linkedInMap = useMemo(
    () => new Map(competitors.map((c) => [c.id, c.linkedInUrl])),
    [competitors]
  );

  const hqMap = useMemo(
    () => new Map(competitors.map((c) => [c.id, c.hqLocation])),
    [competitors]
  );

  const handleCompanyChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      onCompanySelect?.(value === 'current' ? null : parseInt(value, 10));
    },
    [onCompanySelect]
  );

  const rows = useMemo((): CompetitorRow[] => {
    const healthMap = new Map(healthData.map((h) => [h.companyId, h]));

    const unsorted = metrics.map((m) => {
      const sentiment = sentimentMap.get(m.companyId);
      const health = healthMap.get(m.companyId);
      const topAttrs = sentiment
        ? getTopSentimentAttrs(sentiment.scores, 2)
        : [];

      // Prefer real LinkedIn URL from screener; fall back to slugified guess
      const realUrl = linkedInMap.get(m.companyId);
      const fallbackUrl = `https://www.linkedin.com/company/${slugifyForLinkedIn(m.shortName)}`;

      return {
        companyId: m.companyId,
        shortName: m.shortName,
        longName: m.longName,
        headcount: m.headcount,
        salary: m.salary,
        growthRate: m.growthRate,
        attritionRate: m.attritionRate,
        hiringTrend: m.hiringTrend,
        sentimentOverall: sentiment?.scores.overall ?? null,
        topSentimentAttrs: topAttrs,
        stabilityScore: health?.stabilityScore ?? null,
        risk: health?.risk ?? null,
        isCurrentEmployer: m.companyId === currentCompanyId,
        isFormerEmployer: historicalCompanyIds?.has(m.companyId) ?? false,
        linkedInUrl: realUrl ?? fallbackUrl,
        hqLocation: hqMap.get(m.companyId) ?? null,
      };
    });

    // Sort: current employer first, then former employers, then by stability score descending
    return unsorted.sort((a, b) => {
      if (a.isCurrentEmployer && !b.isCurrentEmployer) return -1;
      if (!a.isCurrentEmployer && b.isCurrentEmployer) return 1;
      if (a.isFormerEmployer && !b.isFormerEmployer) return -1;
      if (!a.isFormerEmployer && b.isFormerEmployer) return 1;
      return (b.stabilityScore ?? 0) - (a.stabilityScore ?? 0);
    });
  }, [
    metrics,
    sentimentMap,
    healthData,
    linkedInMap,
    hqMap,
    currentCompanyId,
    historicalCompanyIds,
  ]);

  const columns = useMemo(
    (): ColumnDef<CompetitorRow, unknown>[] => [
      columnHelper.accessor('shortName', {
        header: 'Company',
        cell: (info) => {
          const row = info.row.original;
          let nameColor = 'gray.800';
          if (row.isCurrentEmployer) nameColor = 'blue.600';
          else if (row.isFormerEmployer) nameColor = 'purple.600';

          const peekData: CompanyPeekData = {
            rcid: row.companyId,
            name: row.shortName,
            headcount: row.headcount,
            salary: row.salary,
            growthRate: row.growthRate,
            stabilityScore: row.stabilityScore,
            risk: row.risk,
            sentimentOverall: row.sentimentOverall,
            linkedInUrl: row.linkedInUrl,
          };

          return (
            <Box>
              <HStack spacing={1} align="baseline">
                <CompanyPeekTooltip
                  data={peekData}
                  userSalary={userSalary}
                  userRoleLevel={userRoleLevel ?? null}
                >
                  <Text
                    fontSize="sm"
                    fontWeight={
                      row.isCurrentEmployer || row.isFormerEmployer
                        ? 'bold'
                        : 'normal'
                    }
                    color={nameColor}
                    cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {info.getValue()}
                    {row.isCurrentEmployer ? ' (You)' : ''}
                    {row.isFormerEmployer ? ' (Former)' : ''}
                  </Text>
                </CompanyPeekTooltip>
                {row.linkedInUrl && (
                  <Link
                    href={row.linkedInUrl}
                    isExternal
                    fontSize="xs"
                    color="blue.400"
                    _hover={{ color: 'blue.600' }}
                    title="View on LinkedIn"
                    lineHeight="1"
                  >
                    <span role="img" aria-label="LinkedIn link">
                      🔗
                    </span>
                  </Link>
                )}
              </HStack>
            </Box>
          );
        },
        size: 170,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('hqLocation', {
        header: 'HQ',
        cell: (info) => {
          const v = info.getValue();
          return v ? (
            <Text fontSize="xs" color="gray.600" noOfLines={1}>
              {v}
            </Text>
          ) : (
            '—'
          );
        },
        size: 140,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('headcount', {
        header: 'Headcount',
        cell: (info) => {
          const v = info.getValue();
          return v !== null ? intFormatter(v) : '—';
        },
        size: 100,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('salary', {
        header: 'Median Salary',
        cell: (info) => {
          const v = info.getValue();
          return v !== null ? salaryFormatter(v) : '—';
        },
        size: 120,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('growthRate', {
        header: 'Growth',
        cell: (info) => {
          const v = info.getValue();
          if (v === null) return '—';
          let color = 'gray.500';
          if (v > 0) color = 'green.500';
          else if (v < 0) color = 'red.500';
          return (
            <Text fontSize="sm" color={color}>
              {pctFormatter(v)}
            </Text>
          );
        },
        size: 90,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('attritionRate', {
        header: 'Attrition',
        cell: (info) => {
          const v = info.getValue();
          if (v === null) return '—';
          let color = 'green.500';
          if (v > 0.15) color = 'red.500';
          else if (v > 0.1) color = 'orange.500';
          return (
            <Text fontSize="sm" color={color}>
              {pctFormatter(v)}
            </Text>
          );
        },
        size: 90,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('hiringTrend', {
        header: 'Hiring',
        cell: (info) => TREND_LABELS[info.getValue()],
        size: 100,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('sentimentOverall', {
        header: 'Sentiment',
        cell: (info) => {
          const row = info.row.original;
          const v = info.getValue();
          if (v === null) return '—';
          const color = getSentimentColor(v);
          return (
            <Tooltip
              label={
                row.topSentimentAttrs.length > 0
                  ? `${row.topSentimentAttrs.map((a) => `${a.label}: ${toRating(a.value)}/5`).join(' · ')} — Click for details`
                  : 'Click to view all attributes'
              }
              fontSize="xs"
              hasArrow
            >
              <Text
                fontSize="sm"
                color={color}
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={() => onSentimentClick(row.companyId, row.shortName)}
              >
                {toRating(v)}/5
              </Text>
            </Tooltip>
          );
        },
        size: 90,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.display({
        id: 'topAttributes',
        header: 'Top Attributes',
        cell: (info) => {
          const row = info.row.original;
          if (row.topSentimentAttrs.length === 0) return '—';
          return (
            <HStack spacing={1} flexWrap="wrap">
              {row.topSentimentAttrs.map((attr) => {
                const rating = toRating(attr.value);
                const isGood = attr.value >= 0.7;
                return (
                  <Box
                    key={attr.label}
                    px={1.5}
                    py={0.5}
                    borderRadius="sm"
                    bg={isGood ? 'green.50' : 'orange.50'}
                    border="1px solid"
                    borderColor={isGood ? 'green.200' : 'orange.200'}
                  >
                    <Text
                      fontSize="xs"
                      color={isGood ? 'green.700' : 'orange.600'}
                      whiteSpace="nowrap"
                    >
                      {attr.label} {rating}
                    </Text>
                  </Box>
                );
              })}
            </HStack>
          );
        },
        size: 200,
      }) as ColumnDef<CompetitorRow, unknown>,
      columnHelper.accessor('stabilityScore', {
        header: 'Stability',
        cell: (info) => {
          const row = info.row.original;
          const v = info.getValue();
          if (v === null) return '—';
          const color = row.risk ? RISK_COLORS_HEX[row.risk] : '#718096';
          return (
            <Text fontSize="sm" fontWeight="semibold" color={color}>
              {v}/100
            </Text>
          );
        },
        size: 90,
      }) as ColumnDef<CompetitorRow, unknown>,
    ],
    [salaryFormatter, onSentimentClick, userSalary, userRoleLevel]
  );

  const showCompanySelector =
    workHistoryCompanies && workHistoryCompanies.length > 1 && onCompanySelect;

  // Render the header with dropdown — always visible even during loading
  const headerElement = (
    <HStack justify="space-between" align="center" mb={3}>
      <Box>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Competitor Comparison
        </Text>
        <Text fontSize="xs" color="gray.500" mt={0.5}>
          {isIndustryFallback
            ? 'Showing companies in the same RICS industry and geography, filtered by headcount and sorted by salary.'
            : 'Based on talent flow competitors (companies that share the most employee movement), filtered by headcount and sorted by salary.'}
        </Text>
      </Box>
      {showCompanySelector && (
        <HStack spacing={2} align="center">
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
            Competitors of:
          </Text>
          <Select
            size="xs"
            maxW="220px"
            value={
              selectedCompanyRcid === currentCompanyId ||
              selectedCompanyRcid === null
                ? 'current'
                : String(selectedCompanyRcid)
            }
            onChange={handleCompanyChange}
            borderColor="gray.300"
            _hover={{ borderColor: 'gray.400' }}
          >
            {workHistoryCompanies.map((c) => (
              <option
                key={c.rcid}
                value={c.isCurrent ? 'current' : String(c.rcid)}
              >
                {c.companyName}
                {c.isCurrent
                  ? ' (Current)'
                  : ` (${formatDateRange(c.startDate, c.endDate)})`}
              </option>
            ))}
          </Select>
        </HStack>
      )}
    </HStack>
  );

  if (isLoading) {
    return (
      <Box>
        {headerElement}
        <Skeleton height="300px" borderRadius="md" />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box>
        {headerElement}
        <Box bg="gray.50" p={4} borderRadius="md" textAlign="center">
          <Text color="gray.500" fontSize="sm">
            No competitor data available for this company. Try selecting a
            different company above.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {headerElement}
      {isIndustryFallback && (
        <Text fontSize="xs" color="orange.500" mb={2}>
          No direct competitors found — showing companies in the same industry
          instead.
        </Text>
      )}
      <Box
        border="1px solid"
        borderColor="gray.100"
        borderRadius="md"
        overflow="hidden"
      >
        <TableChart data={rows} columns={columns} showZebraStripe />
      </Box>

      <SentimentDetailModal
        isOpen={isOpen}
        onClose={onClose}
        companyRcid={modalCompanyRcid}
        companyName={modalCompanyName}
      />
    </Box>
  );
};
